/**
 * Turning lever results into the numbers a report uses: one total per parcel
 * for levers 1 and 2 without counting a parcel twice, and an annual estimate.
 */
import type { Lever1ParcelResult } from "./lever1.ts";
import type { Lever2ParcelResult } from "./lever2.ts";
import type { Pence } from "./types.ts";

export interface CombinedSavings {
  /** Billing errors: charged above the rate card (lever 1) */
  overchargePence: Pence;
  /** Moving parcels into a cheaper band, by right-sizing (lever 2) or a near-band fix (lever 1), whichever is larger per parcel */
  bandSavingPence: Pence;
  totalPence: Pence;
  parcelsWithSaving: number;
}

/**
 * Lever 1's near-band saving and lever 2's right-sizing saving both come from a
 * parcel dropping to a cheaper band, so for each parcel only the larger counts.
 * Overcharges are a separate billing issue and add on top.
 */
export function combineLevers12(l1: Lever1ParcelResult[], l2: Lever2ParcelResult[]): CombinedSavings {
  const l2ByOrder = new Map<string, Lever2ParcelResult>();
  for (const r of l2) l2ByOrder.set(r.orderId, r);
  let over = 0;
  let band = 0;
  let parcels = 0;
  const seen = new Set<string>();
  for (const r of l1) {
    seen.add(r.orderId);
    const o = r.flags.includes("overcharged") && r.differencePence ? r.differencePence : 0;
    const near = r.nearBand?.savingPence ?? 0;
    const rs = l2ByOrder.get(r.orderId);
    const right = rs && rs.status === "saving" ? (rs.netSavingPence ?? rs.postageSavingPence) : 0;
    const b = Math.max(near, right);
    over += o;
    band += b;
    if (o + b > 0) parcels++;
  }
  for (const rs of l2) {
    if (seen.has(rs.orderId) || rs.status !== "saving") continue;
    const right = rs.netSavingPence ?? rs.postageSavingPence;
    band += right;
    if (right > 0) parcels++;
  }
  return { overchargePence: over, bandSavingPence: band, totalPence: over + band, parcelsWithSaving: parcels };
}

/** Days covered by the data, counting both ends. */
export function periodDays(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 864e5) + 1;
}

/**
 * Scale a saving seen over the data period to a year. An estimate only: it
 * assumes volumes and mix stay the same. Returns null for periods under 28 days,
 * which are too short to scale up honestly.
 */
export function annualise(pence: Pence, from: string | null, to: string | null): Pence | null {
  if (!from || !to) return null;
  const days = periodDays(from, to);
  if (days < 28) return null;
  return Math.round((pence * 365) / days);
}
